import { reactive } from 'vue'
import { createRouter, createWebHashHistory, RouteLocationNormalized, Router, RouteRecordRaw, RouterScrollBehavior } from 'vue-router'

type setupRouterOptions = {
  base: string,
  scrollBehavior?: RouterScrollBehavior,
  addRouterHooks?: (router: Router) => void
}

/**
 * local route loading timeout
 */
let routeloadingTimeout: ReturnType<typeof setTimeout>

/**
 * A default naive implementation of a scrollbehavior
 */
const defaultScrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) {
    return savedPosition
  } else if (to.name !== from.name) {
    return { left: 0, top: 0 }
  }
}

export const routes: RouteRecordRaw[] = [
  {
    name: 'Home',
    path: '/',
    component: () => import('./pages/HomePage.vue')
  },
  {
    name: 'Editorial',
    props: true,
    path: '/editorial/:pageSlug',
    component: () => import('./pages/EditorialPage.vue')
  },
  {
    // matches /anyroute (if not found) and redirects it to /
    // https://next.router.vuejs.org/guide/migration/#removed-star-or-catch-all-routes
    path: '/:pathMatch(.*)*',
    // flag this route as a 404 page
    // so that (optional) internationalization can replace
    // this with its own implementation
    meta: reactive({ is404: true }),
    redirect: to => {
      return { path: '/' }
    }
  }
]

/**
 * Returns a router instance with attached hooks for tracking page loads,
 * also accepts a custom scrollBehavior
 **/
export const setupRouter = (
  routes: RouteRecordRaw[],
  { base, scrollBehavior, addRouterHooks }: setupRouterOptions
) => {
  /**
   * HASH NAVIGATION
   *
   * When hash mode navigation is required, uncomment the following
   * code and switch to hash history when creating router below
   */
  // If the hash is missing but a pathname is defined this will be used as hash (/foo --> /#/foo)
  if (!window.location.hash && window.location.pathname) {
    history.replaceState(null, null, `/#${window.location.href.replace(window.location.origin, '')}`)
  }

  const router = createRouter({
    // Provide the history implementation to use
    history: createWebHashHistory(base),
    // or
    // history: createWebHistory(base),
    routes, // short for `routes: routes`
    scrollBehavior: scrollBehavior || defaultScrollBehavior
  })

  addRouterHooks && addRouterHooks(router)

  router.beforeEach(async (to, from, next) => {
    // 100ms timeout
    routeloadingTimeout = setTimeout(() => {
      // console.log('route has been loading for more than 100ms, mark is as "loadingChunk"')
      if (!to.meta) to.meta = reactive({ loadingChunk: true })
      to.meta.loadingChunk = true
      if (!from.meta) from.meta = reactive({ loadingChunk: true })
      from.meta.loadingChunk = true
    }, 100)

    // console.time('route chunk loading time')
    if (!hasQueryParams(to) && hasQueryParams(from)) {
      next({ ...to, query: from.query })
    } else {
      next()
    }
  })

  router.beforeResolve((to, from, next) => {
    clearTimeout(routeloadingTimeout)

    if (!to.meta) to.meta = reactive({ loadingChunk: false })
    to.meta.loadingChunk = false
    if (!from.meta) from.meta = reactive({ loadingChunk: false })
    from.meta.loadingChunk = false

    // console.timeEnd('route chunk loading time')
    next()
  })
  return router
}

function hasQueryParams (route: RouteLocationNormalized) {
  return !!Object.keys(route.query).length
}
